'use client';

import { createContext, type ReactNode, useContext } from 'react';

/** A click on a link crumb, as `Breadcrumb` reports it to the nearest `BreadcrumbClickProvider`. */
export interface BreadcrumbClick {
  /** 1-based, counted from the root crumb. */
  readonly position: number;
  /** How many crumbs the trail has. */
  readonly count: number;
  /** The crumb was ellipsized, or clicked from the collapsed-crumbs menu. */
  readonly wasTruncated: boolean;
}

export type BreadcrumbClickReporter = (click: BreadcrumbClick) => void;

const BreadcrumbClickContext = createContext<BreadcrumbClickReporter | undefined>(undefined);

export interface BreadcrumbClickProviderProps {
  /** Runs on every link crumb click of every `Breadcrumb` below, in the row or from the collapsed-crumbs menu. */
  onCrumbClick: BreadcrumbClickReporter;
  children: ReactNode;
}

/**
 * Hands every link crumb click of the `Breadcrumb`s below to one callback, such as an analytics event.
 *
 * @headless Context provider for Breadcrumb click reporting; renders only its children.
 */
export function BreadcrumbClickProvider({ onCrumbClick, children }: BreadcrumbClickProviderProps) {
  return <BreadcrumbClickContext.Provider value={onCrumbClick}>{children}</BreadcrumbClickContext.Provider>;
}

/** The nearest provider's click reporter, or undefined outside one. */
export function useBreadcrumbClickReporter(): BreadcrumbClickReporter | undefined {
  return useContext(BreadcrumbClickContext);
}
